/**
 * Anomalías.
 *
 * «Fuera de servicio» no es una anomalía más: es un equipo que **no
 * protege**. El titular tiene que estar informado por escrito (art. 17.f),
 * y esa fila no puede perderse entre las demás.
 */

import { Link, useSearchParams } from 'react-router-dom';
import { DataTable, type Column } from '../table/data-table.js';
import { useListing } from '../table/use-listing.js';
import { dayOf } from '../table/format.js';

interface Row {
  id: string;
  number: string;
  anomaly_type: string;
  description: string;
  severity: string;
  status: string;
  uncorrectable: boolean;
  out_of_service: boolean;
  detected_at: string;
  customer_name: string;
  site_name: string;
  equipment_code: string | null;
  days_open: number;
}

// Las tres que admite la base (`ck_anomaly_severity`), ni una más.
//
// Antes esta tabla decía CRITICA y MODERADA, que no existen, y le faltaba
// MUY_GRAVE, que sí. El resultado era que la anomalía más grave de todas caía
// en el caso por defecto y salía en gris, con el mismo aspecto que una
// etiqueta despegada — justo al revés de lo que hace falta.
const SEVERITY: Record<string, { label: string; tone: string; icon: string }> = {
  MUY_GRAVE: { label: 'Muy grave', tone: 'bad', icon: '⚠' },
  GRAVE: { label: 'Grave', tone: 'warn', icon: '▲' },
  LEVE: { label: 'Leve', tone: 'off', icon: '■' },
};

const COLUMNS: Column<Row>[] = [
  {
    id: 'number',
    label: 'Nº',
    // Enlazado a su ficha: hasta ahora el listado era un callejón sin salida.
    render: (r) => (
      <Link className="table__link" to={`/anomalias/${r.id}`}>
        {r.number}
      </Link>
    ),
    sortable: true,
    width: '11rem',
  },
  { id: 'customerName', label: 'Cliente', render: (r) => r.customer_name },
  { id: 'siteName', label: 'Centro', render: (r) => r.site_name },
  { id: 'equipmentCode', label: 'Equipo', render: (r) => r.equipment_code ?? '—', width: '8rem' },
  { id: 'description', label: 'Descripción', render: (r) => r.description },
  {
    id: 'severity',
    label: 'Gravedad',
    width: '8rem',
    sortable: true,
    render: (r) => {
      const s = SEVERITY[r.severity] ?? { label: r.severity, tone: 'off', icon: '■' };
      return <span className={`pill pill--${s.tone}`}>{`${s.icon} ${s.label}`}</span>;
    },
    exportValue: (r) => SEVERITY[r.severity]?.label ?? r.severity,
  },
  {
    id: 'outOfService',
    label: 'Servicio',
    width: '11rem',
    render: (r) =>
      r.out_of_service ? (
        <span className="pill pill--bad">⚠ Fuera de servicio</span>
      ) : (
        <span className="pill pill--ok">✓ En servicio</span>
      ),
    exportValue: (r) => (r.out_of_service ? 'Fuera de servicio' : 'En servicio'),
  },
  { id: 'status', label: 'Estado', render: (r) => r.status, width: '10rem' },
  {
    id: 'detectedAt',
    label: 'Detectada',
    render: (r) => dayOf(r.detected_at),
    sortable: true,
    width: '8rem',
  },
  {
    id: 'daysOpen',
    label: 'Días abierta',
    numeric: true,
    width: '8rem',
    render: (r) =>
      r.days_open > 30 ? (
        <span className="due due--late">⚠ {r.days_open}</span>
      ) : (
        <span className="due">{r.days_open}</span>
      ),
    exportValue: (r) => r.days_open,
  },
];

const COLUMN_IDS = COLUMNS.map((c) => c.id);

const QUICK = [
  { id: 'open', label: 'Abiertas', param: { openOnly: 'true' } },
  { id: 'oos', label: 'Fuera de servicio', param: { outOfService: 'true' } },
  { id: 'unfix', label: 'No subsanables', param: { uncorrectable: 'true' } },
  { id: 'all', label: 'Todas', param: {} },
] as const;

export function AnomaliesScreen(): React.ReactNode {
  const [params] = useSearchParams();
  const quick = QUICK.find((q) => q.id === params.get('v')) ?? QUICK[0];

  const listing = useListing<Row>({
    path: '/anomalies',
    columnIds: COLUMN_IDS,
    extraParams: quick.param,
    preserve: ['v'],
  });

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Anomalías</h1>
        <div className="chips" role="group" aria-label="Filtros rápidos">
          {QUICK.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`chip${quick.id === option.id ? ' chip--on' : ''}`}
              aria-pressed={quick.id === option.id}
              onClick={() => listing.setParam('v', option.id === 'open' ? null : option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
        {listing.total !== null && <span className="screen__count">{listing.total} anomalías</span>}
      </header>

      {quick.id === 'oos' && (
        <p className="screen__note">
          Equipos que no protegen. El art. 17.f exige comunicárselo al titular por escrito, y esa
          comunicación se guarda como evidencia.
        </p>
      )}

      {listing.error !== null && (
        <p className="screen__error" role="alert">
          {listing.error}
        </p>
      )}

      <DataTable
        columns={COLUMNS}
        rows={listing.rows}
        idOf={(r) => r.id}
        state={listing.state}
        onStateChange={listing.setState}
        exportName={quick.id === 'oos' ? 'anomalias-fuera-de-servicio' : 'anomalias'}
        loading={listing.loading}
        hasMore={listing.hasMore}
        onLoadMore={listing.loadMore}
        totalMatching={listing.total}
        emptyMessage="No hay anomalías que coincidan."
      />
    </section>
  );
}
